import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { format } from 'date-fns';
import { reportsApi } from '../api/reports.api';
import { Product, InventoryMovement } from '../types';
import PaginatedTable from '../components/common/PaginatedTable';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

interface DishSold {
  recipeId: number;
  recipeName: string;
  salePrice: number;
  quantitySold: number;
  totalRevenue: number;
}

interface DishesSoldResponse {
  dishes: DishSold[];
  summary: {
    totalDishes: number;
    totalRevenue: number;
    uniqueDishes: number;
  };
}

const Reports: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [stockProducts, setStockProducts] = useState<Product[]>([]);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([]);
  const [dishesSold, setDishesSold] = useState<DishesSoldResponse | null>(null);
  const [serviceTimes, setServiceTimes] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState('');
  const [dishesFrom, setDishesFrom] = useState('');
  const [dishesTo, setDishesTo] = useState('');
  const [timesFrom, setTimesFrom] = useState('');
  const [timesTo, setTimesTo] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [stockRes, lowRes] = await Promise.all([
          reportsApi.getStockReport(),
          reportsApi.getLowStockReport(),
        ]);
        setStockProducts(stockRes.data);
        setLowStockProducts(lowRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    const loadMovements = async () => {
      try {
        const params: any = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (filterType) params.type = filterType;
        const res = await reportsApi.getMovementsReport(params);
        setMovements(res.data);
      } catch (err) { console.error(err); }
    };
    if (tab === 1) loadMovements();
  }, [tab, startDate, endDate, filterType]);

  useEffect(() => {
    const loadDishes = async () => {
      try {
        const params: any = {};
        if (dishesFrom) params.from = dishesFrom;
        if (dishesTo) params.to = dishesTo;
        const res = await reportsApi.getDishesSold(params);
        setDishesSold(res.data);
      } catch (err) { console.error(err); }
    };
    if (tab === 3) loadDishes();
  }, [tab, dishesFrom, dishesTo]);

  useEffect(() => {
    const loadTimes = async () => {
      try {
        const params: any = {};
        if (timesFrom) params.from = timesFrom;
        if (timesTo) params.to = timesTo;
        const res = await reportsApi.getServiceTimes(params);
        setServiceTimes(res.data);
      } catch (err) { console.error(err); }
    };
    if (tab === 4) loadTimes();
  }, [tab, timesFrom, timesTo]);

  const typeColors: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
    entrada: 'success', salida: 'error', ajuste: 'warning', venta: 'info', ingreso_mercaderia: 'success', devolucion: 'success',
  };

  if (loading) return <LoadingSpinner />;

  // --- Columnas ---
  const stockColumns = [
    { id: 'name', label: 'Producto', minWidth: 150 },
    { id: 'categoryName', label: 'Categoria', minWidth: 100, format: (_: any, row: any) => row.category?.name || '-' },
    { id: 'unitName', label: 'Unidad', minWidth: 80, format: (_: any, row: any) => row.unitRelation?.abbreviation || row.unit || '-' },
    { id: 'currentStock', label: 'Stock Actual', align: 'right' as const, minWidth: 100 },
    { id: 'minStock', label: 'Stock Minimo', align: 'right' as const, minWidth: 100 },
    {
      id: 'referencePrice', label: 'Precio Ref.', align: 'right' as const, minWidth: 100,
      format: (v: any) => `S/ ${Number(v || 0).toFixed(2)}`,
    },
    {
      id: 'estado', label: 'Estado', minWidth: 100,
      format: (_: any, row: any) => row.currentStock < row.minStock
        ? <Chip label="Bajo stock" size="small" color="error" />
        : <Chip label="OK" size="small" color="success" variant="outlined" />,
    },
  ];

  const movementColumns = [
    {
      id: 'createdAt', label: 'Fecha', minWidth: 130,
      format: (v: any) => v ? format(new Date(v), 'dd/MM/yyyy HH:mm') : '-',
    },
    { id: 'productName', label: 'Producto', minWidth: 140, format: (_: any, row: any) => row.product?.name || '-' },
    {
      id: 'type', label: 'Tipo', minWidth: 120,
      format: (v: string) => (
        <Chip label={v.charAt(0).toUpperCase() + v.slice(1).replace('_', ' ')} size="small" color={typeColors[v] || 'default'} />
      ),
    },
    { id: 'quantity', label: 'Cantidad', align: 'right' as const, minWidth: 80 },
    { id: 'previousStock', label: 'Stock Anterior', align: 'right' as const, minWidth: 100 },
    { id: 'newStock', label: 'Stock Nuevo', align: 'right' as const, minWidth: 100 },
    { id: 'userName', label: 'Usuario', minWidth: 120, format: (_: any, row: any) => row.user?.fullName || '-' },
    { id: 'reason', label: 'Razon', minWidth: 180 },
  ];

  const lowStockColumns = [
    { id: 'name', label: 'Producto', minWidth: 150 },
    { id: 'categoryName', label: 'Categoria', minWidth: 120, format: (_: any, row: any) => row.category?.name || '-' },
    {
      id: 'currentStock', label: 'Stock Actual', align: 'right' as const, minWidth: 100,
      format: (v: any) => <Chip label={v} size="small" color="error" />,
    },
    { id: 'minStock', label: 'Stock Minimo', align: 'right' as const, minWidth: 100 },
    {
      id: 'deficit', label: 'Deficit', align: 'right' as const, minWidth: 80,
      format: (_: any, row: any) => <Typography sx={{ color: '#EF4444', fontWeight: 700 }}>-{row.minStock - row.currentStock}</Typography>,
    },
    { id: 'supplierName', label: 'Proveedor', minWidth: 130, format: (_: any, row: any) => row.supplier?.name || '-' },
  ];

  const dishesColumns = [
    { id: 'pos', label: '#', minWidth: 40, format: (_: any, __: any, idx?: number) => (idx ?? 0) + 1 },
    { id: 'recipeName', label: 'Plato', minWidth: 180 },
    {
      id: 'salePrice', label: 'Precio Unit.', align: 'right' as const, minWidth: 100,
      format: (v: any) => `S/ ${Number(v).toFixed(2)}`,
    },
    {
      id: 'quantitySold', label: 'Cantidad', align: 'right' as const, minWidth: 100,
      format: (v: any) => <Chip label={v} size="small" color="primary" />,
    },
    {
      id: 'totalRevenue', label: 'Ingreso Total', align: 'right' as const, minWidth: 120,
      format: (v: any) => <strong>S/ {Number(v).toFixed(2)}</strong>,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Reportes</Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Stock Actual" />
          <Tab label="Movimientos" />
          <Tab label="Bajo Stock" />
          <Tab label="Platos Vendidos" />
          <Tab label="Tiempos de Servicio" />
        </Tabs>
      </Paper>

      {/* Stock Actual */}
      <TabPanel value={tab} index={0}>
        <PaginatedTable
          columns={stockColumns}
          rows={stockProducts}
          exportFileName="reporte_stock"
          searchFields={['name']}
          defaultRowsPerPage={25}
        />
      </TabPanel>

      {/* Movimientos */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Desde" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Hasta" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={filterType} label="Tipo" onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="entrada">Entrada</MenuItem>
              <MenuItem value="salida">Salida</MenuItem>
              <MenuItem value="ajuste">Ajuste</MenuItem>
              <MenuItem value="venta">Venta</MenuItem>
              <MenuItem value="devolucion">Devolucion</MenuItem>
              <MenuItem value="ingreso_mercaderia">Ingreso Mercaderia</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <PaginatedTable
          columns={movementColumns}
          rows={movements}
          exportFileName="reporte_movimientos"
          searchFields={['reason']}
          defaultRowsPerPage={25}
        />
      </TabPanel>

      {/* Bajo Stock */}
      <TabPanel value={tab} index={2}>
        <PaginatedTable
          columns={lowStockColumns}
          rows={lowStockProducts}
          exportFileName="reporte_bajo_stock"
          searchFields={['name']}
          emptyMessage="No hay productos con bajo stock"
        />
      </TabPanel>

      {/* Platos Vendidos */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Desde" type="date" value={dishesFrom} onChange={(e) => setDishesFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Hasta" type="date" value={dishesTo} onChange={(e) => setDishesTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>

        {dishesSold && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Total Platos Vendidos</Typography>
                  <Typography variant="h4" fontWeight={700} color="primary">{dishesSold.summary.totalDishes}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Ingresos por Platos</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#10B981' }}>S/ {dishesSold.summary.totalRevenue.toFixed(2)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Platos Diferentes</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#F59E0B' }}>{dishesSold.summary.uniqueDishes}</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            <PaginatedTable
              columns={dishesColumns}
              rows={dishesSold.dishes}
              rowKey="recipeId"
              exportFileName="reporte_platos_vendidos"
              searchFields={['recipeName']}
              emptyMessage="No hay platos vendidos en el rango"
            />
          </>
        )}
      </TabPanel>

      {/* Tiempos de Servicio - mantiene layout especial con cards + sub-tablas */}
      <TabPanel value={tab} index={4}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Desde" type="date" value={timesFrom} onChange={(e) => setTimesFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Hasta" type="date" value={timesTo} onChange={(e) => setTimesTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>

        {serviceTimes && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Pedidos', value: serviceTimes.summary.totalOrders, color: 'primary' },
                { label: 'Tiempo Promedio', value: `${serviceTimes.summary.avgTimeMinutes} min`, color: '#F59E0B' },
                { label: 'Mas Rapido', value: `${serviceTimes.summary.fastestMinutes} min`, color: '#10B981' },
                { label: 'Mas Lento', value: `${serviceTimes.summary.slowestMinutes} min`, color: '#EF4444' },
              ].map((item) => (
                <Grid key={item.label} size={{ xs: 12, sm: 3 }}>
                  <Card><CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: item.color }}>{item.value}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>

            {serviceTimes.orders.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No hay pedidos pagados en el rango</Typography>
              </Paper>
            ) : (
              serviceTimes.orders.map((o: any) => (
                <Paper key={o.id} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" fontWeight={700}>{o.code}</Typography>
                      <Chip label={o.table} size="small" color="primary" />
                      <Chip label={`Total: ${o.totalMinutes ?? '-'} min`} size="small"
                        color={o.totalMinutes <= 15 ? 'success' : o.totalMinutes <= 30 ? 'warning' : 'error'} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(o.createdAt), 'dd/MM HH:mm')} → {format(new Date(o.paidAt), 'dd/MM HH:mm')}
                      {' | '}Mesero: {o.waiter} | S/ {Number(o.total).toFixed(2)}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Plato</TableCell>
                          <TableCell align="center">Cant.</TableCell>
                          <TableCell align="center">Pedido</TableCell>
                          <TableCell align="center">Espera</TableCell>
                          <TableCell align="center">Preparacion</TableCell>
                          <TableCell align="center">Listo</TableCell>
                          <TableCell align="center">Servido</TableCell>
                          <TableCell align="center">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {o.items.map((item: any) => (
                          <TableRow key={item.id}>
                            <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                              {item.pedidoAt ? format(new Date(item.pedidoAt), 'HH:mm') : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {item.esperaCocina != null ? <Chip label={`${item.esperaCocina}m`} size="small" variant="outlined"
                                color={item.esperaCocina <= 5 ? 'success' : item.esperaCocina <= 10 ? 'warning' : 'error'} /> : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {item.preparacion != null ? <Chip label={`${item.preparacion}m`} size="small" variant="outlined"
                                color={item.preparacion <= 10 ? 'success' : item.preparacion <= 20 ? 'warning' : 'error'} /> : '-'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                              {item.listoAt ? format(new Date(item.listoAt), 'HH:mm') : '-'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                              {item.servidoAt ? format(new Date(item.servidoAt), 'HH:mm') : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {item.totalItem != null ? <Chip label={`${item.totalItem}m`} size="small"
                                color={item.totalItem <= 15 ? 'success' : item.totalItem <= 30 ? 'warning' : 'error'} /> : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))
            )}
          </>
        )}
      </TabPanel>
    </Box>
  );
};

export default Reports;
